import { RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Alert } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import { usePwaStore } from '@/shared/stores/pwaStore';
import { AlertVariant } from '@/shared/types/alert.types';
import { ButtonSize, ButtonVariant } from '@/shared/types/button.types';

interface ServiceWorkerUpdateProps {
  readonly needRefresh: boolean;
  readonly offlineReady: boolean;
  readonly onUpdate: () => void;
  readonly onClose: () => void;
}

/**
 * Service worker update notification component.
 *
 * Displays alerts for:
 * - New version available (with update button)
 * - App ready for offline use
 *
 * Follows design system (Alert, Button components)
 * WCAG 2.1 AA compliant with proper ARIA attributes.
 *
 * @example
 * ```tsx
 * <ServiceWorkerUpdate
 *   needRefresh={needRefresh}
 *   offlineReady={offlineReady}
 *   onUpdate={() => updateServiceWorker(true)}
 *   onClose={() => setShowAlert(false)}
 * />
 * ```
 */
export function ServiceWorkerUpdate({
  needRefresh,
  offlineReady,
  onUpdate,
  onClose,
}: ServiceWorkerUpdateProps) {
  if (!needRefresh && !offlineReady) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      {needRefresh && (
        <Alert
          variant={AlertVariant.Info}
          icon={<RefreshCw className="size-4" aria-hidden="true" />}
          className="mb-2 shadow-lg"
          role="alert"
          aria-live="polite"
        >
          <div className="flex-1">
            <h3 className="font-semibold">Update Available</h3>
            <p className="text-sm text-muted-foreground">
              A new version of the app is ready. Refresh to update.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size={ButtonSize.Small}
              variant={ButtonVariant.Default}
              onClick={onUpdate}
              aria-label="Update application"
            >
              Update Now
            </Button>
            <Button
              size={ButtonSize.Small}
              variant={ButtonVariant.Ghost}
              onClick={onClose}
              aria-label="Dismiss update notification"
            >
              Later
            </Button>
          </div>
        </Alert>
      )}

      {offlineReady && !needRefresh && (
        <Alert
          variant={AlertVariant.Success}
          icon={<Wifi className="size-4" aria-hidden="true" />}
          className="shadow-lg"
          role="status"
          aria-live="polite"
        >
          <div className="flex-1">
            <h3 className="font-semibold">Ready for Offline Use</h3>
            <p className="text-sm text-muted-foreground">
              App is now available offline. You can work without internet.
            </p>
          </div>
          <Button
            size={ButtonSize.Small}
            variant={ButtonVariant.Ghost}
            onClick={onClose}
            aria-label="Dismiss offline notification"
          >
            Got it
          </Button>
        </Alert>
      )}
    </div>
  );
}

/**
 * Optional: Offline indicator component (persistent in header)
 */
interface OfflineIndicatorProps {
  readonly isOnline: boolean;
}

export function OfflineIndicator({ isOnline }: OfflineIndicatorProps) {
  if (isOnline) return null;

  return (
    <div
      className="flex items-center gap-2 rounded-md bg-warning/10 px-3 py-1.5 text-sm text-warning"
      role="status"
      aria-live="polite"
    >
      <WifiOff className="size-4" aria-hidden="true" />
      <span>Offline Mode</span>
    </div>
  );
}

/**
 * Compact badge shown in the header when an update was dismissed.
 * Allows the user to still trigger the update without the full alert.
 */
export function UpdateAvailableBadge() {
  const needRefresh = usePwaStore((s) => s.needRefresh);
  const updateDismissed = usePwaStore((s) => s.updateDismissed);
  const updateServiceWorker = usePwaStore((s) => s.updateServiceWorker);

  if (!needRefresh || !updateDismissed || !updateServiceWorker) return null;

  return (
    <button
      type="button"
      onClick={() => {
        updateServiceWorker(true).catch((error) => {
          console.error('[SW] Update failed:', error);
        });
      }}
      className="flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
      aria-label="Apply pending application update"
    >
      <RefreshCw className="size-3.5" aria-hidden="true" />
      <span>Update</span>
    </button>
  );
}
